import { useState, useRef, useEffect } from 'react';
import { Download, Loader2, Check, Sliders, Sparkles, RotateCcw, Wand2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// High-resolution SVG logo generator with proper spacing
const DownloadStatus = ({ busy, done, spinClass, hoverClass }) => {
  if (busy) return <Loader2 className={`animate-spin ${spinClass}`} size={20} />;
  if (done) return <Check className="text-emerald-400" size={20} />;
  return <Download className={`text-slate-500 ${hoverClass} transition-colors`} size={20} />;
};

const generateLogoSVG = (width = 800, height = 200, filters = {}) => {
  const { brightness = 100, contrast = 100, saturate = 100, hueRotate = 0 } = filters;
  const filterStyle = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturate}%) hue-rotate(${hueRotate}deg)`;
  
  return `<?xml version="1.0" encoding="UTF-8"?>
<svg width="${width}" height="${height}" viewBox="0 0 800 200" xmlns="http://www.w3.org/2000/svg" style="filter: ${filterStyle}">
  <defs>
    <linearGradient id="iconGradient" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" style="stop-color:#7c3aed;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#8b5cf6;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#06b6d4;stop-opacity:1" />
    </linearGradient>
    <linearGradient id="textGradient" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" style="stop-color:#8b5cf6;stop-opacity:1" />
      <stop offset="50%" style="stop-color:#06b6d4;stop-opacity:1" />
      <stop offset="100%" style="stop-color:#8b5cf6;stop-opacity:1" />
    </linearGradient>
    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
      <feMerge>
        <feMergeNode in="coloredBlur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>
    <filter id="shadow">
      <feDropShadow dx="0" dy="2" stdDeviation="3" flood-opacity="0.3"/>
    </filter>
  </defs>
  
  <rect width="800" height="200" fill="transparent"/>
  
  <!-- Icon container -->
  <rect x="25" y="35" width="130" height="130" rx="28" ry="28" fill="url(#iconGradient)" filter="url(#glow)"/>
  
  <!-- Wi-Fi Signal Icon - Clean symmetric arcs -->
  <g transform="translate(90, 100)" fill="none" stroke="white" stroke-width="5" stroke-linecap="round">
    <!-- Outer arc - using elliptical arc command for perfect curve -->
    <path d="M -32 -8 A 40 40 0 0 1 32 -8"/>
    <!-- Middle arc -->
    <path d="M -22 2 A 28 28 0 0 1 22 2"/>
    <!-- Inner arc -->
    <path d="M -12 12 A 16 16 0 0 1 12 12"/>
    <!-- Center dot -->
    <circle cx="0" cy="24" r="5" fill="white" stroke="none"/>
  </g>
  
  <!-- MIGHTY - with proper letter spacing -->
  <text x="175" y="112" font-family="'Arial Black', 'Helvetica Neue', Arial, sans-serif" font-size="68" font-weight="900" fill="#D1D5DB" letter-spacing="4" filter="url(#shadow)">MIGHTY</text>
  
  <!-- LINK - gradient text with spacing -->
  <text x="502" y="112" font-family="'Arial Black', 'Helvetica Neue', Arial, sans-serif" font-size="68" font-weight="900" fill="url(#textGradient)" letter-spacing="4" filter="url(#shadow)">LINK</text>
  
  <!-- Field Services tagline -->
  <text x="178" y="148" font-family="Arial, 'Helvetica Neue', sans-serif" font-size="15" font-weight="500" fill="#64748b" letter-spacing="5.5" text-transform="uppercase">FIELD SERVICES</text>
</svg>`;
};

// Generate PNG from SVG with filters
const generateLogoPNG = async (width = 1600, height = 400, darkBg = true, filters = {}) => {
  return new Promise((resolve) => {
    const svgString = generateLogoSVG(width, height, filters);
    const img = new Image();
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    img.onload = () => {
      if (darkBg) {
        ctx.fillStyle = '#0A0A0F';
        ctx.fillRect(0, 0, width, height);
      }
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgString)));
  });
};

export const LogoDownload = ({ variant = 'card' }) => {
  const [downloading, setDownloading] = useState(null);
  const [downloaded, setDownloaded] = useState(null);
  const [activeTab, setActiveTab] = useState('download'); // 'download', 'filters', 'ai'
  const [filters, setFilters] = useState({
    brightness: 100,
    contrast: 100,
    saturate: 100,
    hueRotate: 0
  });
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiImage, setAiImage] = useState(null);
  const previewRef = useRef(null);
  const { getAuthHeaders } = useAuth();

  const resetFilters = () => {
    setFilters({ brightness: 100, contrast: 100, saturate: 100, hueRotate: 0 });
  };

  const downloadFile = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = async (format) => {
    setDownloading(format);
    setDownloaded(null);
    
    try {
      let dataUrl;
      let filename;
      
      switch (format) {
        case 'svg':
          const svgContent = generateLogoSVG(800, 200, filters);
          dataUrl = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgContent)));
          filename = 'mighty-link-logo.svg';
          break;
        case 'png-dark':
          dataUrl = await generateLogoPNG(1600, 400, true, filters);
          filename = 'mighty-link-logo-dark-bg.png';
          break;
        case 'png-transparent':
          dataUrl = await generateLogoPNG(1600, 400, false, filters);
          filename = 'mighty-link-logo-transparent.png';
          break;
        case 'ai':
          if (aiImage) {
            dataUrl = `data:image/png;base64,${aiImage}`;
            filename = 'mighty-link-logo-ai-generated.png';
          }
          break;
        default:
          return;
      }
      
      if (dataUrl) {
        downloadFile(dataUrl, filename);
        setDownloaded(format);
        setTimeout(() => setDownloaded(null), 2000);
      }
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Please enter a description for the logo edit');
      return;
    }

    setAiGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/admin/edit-logo`,
        { edit_instruction: aiPrompt },
        { headers: getAuthHeaders() }
      );
      
      if (response.data.success && response.data.image_base64) {
        setAiImage(response.data.image_base64);
        toast.success('AI logo generated successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate AI logo');
    } finally {
      setAiGenerating(false);
    }
  };

  const filterStyle = {
    filter: `brightness(${filters.brightness}%) contrast(${filters.contrast}%) saturate(${filters.saturate}%) hue-rotate(${filters.hueRotate}deg)`
  };

  return (
    <div className="bg-[#12121A] rounded-xl border border-slate-800 p-4 sm:p-6 max-w-lg w-full" data-testid="logo-download-card">
      <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
        Logo Studio
      </h3>
      <p className="text-slate-400 text-sm mb-4">
        Download, customize, or generate AI-powered logos
      </p>
      
      {/* Tab Navigation */}
      <div className="flex gap-1 bg-[#0A0A0F] rounded-lg p-1 mb-6">
        {[
          { id: 'download', label: 'Download', icon: Download },
          { id: 'filters', label: 'Filters', icon: Sliders },
          { id: 'ai', label: 'AI Edit', icon: Sparkles }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
              activeTab === id 
                ? 'bg-violet-500/20 text-violet-300' 
                : 'text-slate-400 hover:text-white'
            }`}
            data-testid={`tab-${id}`}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>
      
      {/* Logo Preview */}
      <div 
        ref={previewRef}
        className="bg-[#0A0A0F] rounded-lg p-4 sm:p-6 mb-6 flex items-center justify-center border border-slate-700 min-h-[120px] overflow-hidden"
        style={activeTab === 'filters' ? filterStyle : {}}
      >
        {activeTab === 'ai' && aiImage ? (
          <img 
            src={`data:image/png;base64,${aiImage}`} 
            alt="AI Generated Logo" 
            className="max-h-24 object-contain"
          />
        ) : (
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <div className="relative flex-shrink-0">
              <div className="absolute inset-0 rounded-xl bg-gradient-to-br from-violet-500 to-cyan-500 blur-md opacity-60"></div>
              <div className="relative w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-violet-600 via-violet-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6 sm:w-8 sm:h-8">
                  <path d="M5 12.55a11 11 0 0 1 14.08 0" />
                  <path d="M1.42 9a16 16 0 0 1 21.16 0" />
                  <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
                  <circle cx="12" cy="20" r="1" fill="white" />
                </svg>
              </div>
            </div>
            {/* Text - Properly spaced */}
            <div className="flex flex-col leading-none">
              <span className="font-extrabold text-lg sm:text-2xl tracking-wide whitespace-nowrap" style={{ fontFamily: 'Syne, sans-serif', letterSpacing: '0.05em' }}>
                <span className="text-slate-300">MIGHTY</span>
                <span className="ml-1 bg-gradient-to-r from-violet-500 via-cyan-400 to-violet-500 bg-clip-text text-transparent">LINK</span>
              </span>
              <span className="text-[10px] text-slate-500 tracking-[0.25em] uppercase mt-1">
                Field Services
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Download Tab */}
      {activeTab === 'download' && (
        <div className="space-y-3">
          <button
            onClick={() => handleDownload('svg')}
            disabled={downloading}
            className="w-full flex items-center justify-between p-4 bg-[#0A0A0F] rounded-lg border border-slate-700 hover:border-violet-500/50 transition-colors group"
            data-testid="download-svg"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                <span className="text-violet-400 text-xs font-bold">SVG</span>
              </div>
              <div className="text-left">
                <p className="font-medium text-white">Vector Format (SVG)</p>
                <p className="text-xs text-slate-500">Scalable, best for print & web</p>
              </div>
            </div>
            <DownloadStatus busy={downloading === 'svg'} done={downloaded === 'svg'} spinClass="text-violet-400" hoverClass="group-hover:text-violet-400" />
          </button>
          
          <button
            onClick={() => handleDownload('png-transparent')}
            disabled={downloading}
            className="w-full flex items-center justify-between p-4 bg-[#0A0A0F] rounded-lg border border-slate-700 hover:border-violet-500/50 transition-colors group"
            data-testid="download-png-transparent"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <span className="text-cyan-400 text-xs font-bold">PNG</span>
              </div>
              <div className="text-left">
                <p className="font-medium text-white">Transparent Background</p>
                <p className="text-xs text-slate-500">1600×400px, high resolution</p>
              </div>
            </div>
            <DownloadStatus busy={downloading === 'png-transparent'} done={downloaded === 'png-transparent'} spinClass="text-cyan-400" hoverClass="group-hover:text-cyan-400" />
          </button>
          
          <button
            onClick={() => handleDownload('png-dark')}
            disabled={downloading}
            className="w-full flex items-center justify-between p-4 bg-[#0A0A0F] rounded-lg border border-slate-700 hover:border-violet-500/50 transition-colors group"
            data-testid="download-png-dark"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-slate-700 rounded-lg flex items-center justify-center">
                <span className="text-slate-300 text-xs font-bold">PNG</span>
              </div>
              <div className="text-left">
                <p className="font-medium text-white">Dark Background</p>
                <p className="text-xs text-slate-500">1600×400px, with dark bg</p>
              </div>
            </div>
            <DownloadStatus busy={downloading === 'png-dark'} done={downloaded === 'png-dark'} spinClass="text-slate-400" hoverClass="group-hover:text-slate-300" />
          </button>
        </div>
      )}

      {/* Filters Tab */}
      {activeTab === 'filters' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-400">Adjust logo appearance</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={resetFilters}
              className="text-slate-400 hover:text-white"
            >
              <RotateCcw size={14} className="mr-1" />
              Reset
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-300 text-sm">Brightness</Label>
                <span className="text-xs text-slate-500">{filters.brightness}%</span>
              </div>
              <input
                type="range"
                min="50"
                max="150"
                value={filters.brightness}
                onChange={(e) => setFilters(f => ({ ...f, brightness: parseInt(e.target.value) }))}
                className="w-full accent-violet-500"
                data-testid="filter-brightness"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-300 text-sm">Contrast</Label>
                <span className="text-xs text-slate-500">{filters.contrast}%</span>
              </div>
              <input
                type="range"
                min="50"
                max="150"
                value={filters.contrast}
                onChange={(e) => setFilters(f => ({ ...f, contrast: parseInt(e.target.value) }))}
                className="w-full accent-violet-500"
                data-testid="filter-contrast"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-300 text-sm">Saturation</Label>
                <span className="text-xs text-slate-500">{filters.saturate}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="200"
                value={filters.saturate}
                onChange={(e) => setFilters(f => ({ ...f, saturate: parseInt(e.target.value) }))}
                className="w-full accent-cyan-500"
                data-testid="filter-saturate"
              />
            </div>

            <div>
              <div className="flex justify-between mb-2">
                <Label className="text-slate-300 text-sm">Hue Rotate</Label>
                <span className="text-xs text-slate-500">{filters.hueRotate}°</span>
              </div>
              <input
                type="range"
                min="0"
                max="360"
                value={filters.hueRotate}
                onChange={(e) => setFilters(f => ({ ...f, hueRotate: parseInt(e.target.value) }))}
                className="w-full accent-emerald-500"
                data-testid="filter-hue"
              />
            </div>
          </div>

          <div className="pt-4 border-t border-slate-700">
            <Button
              onClick={() => handleDownload('png-transparent')}
              disabled={downloading}
              className="w-full btn-gradient text-white"
            >
              {downloading ? <Loader2 className="animate-spin mr-2" size={18} /> : <Download size={18} className="mr-2" />}
              Download with Filters
            </Button>
          </div>
        </div>
      )}

      {/* AI Edit Tab */}
      {activeTab === 'ai' && (
        <div className="space-y-4">
          <div>
            <Label className="text-slate-300 text-sm mb-2 block">Describe your logo edit</Label>
            <Textarea
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              placeholder="E.g., Make it more futuristic with neon glow effects, add a 3D look, change colors to blue and gold..."
              className="bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-500 min-h-[100px]"
              data-testid="ai-prompt"
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            {['Minimalist style', 'Neon glow effect', '3D metallic look', 'Retro vintage'].map((suggestion) => (
              <button
                key={suggestion}
                onClick={() => setAiPrompt(suggestion)}
                className="text-xs p-2 bg-[#0A0A0F] border border-slate-700 rounded-lg text-slate-400 hover:text-white hover:border-violet-500/50 transition-colors text-left"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <Button
            onClick={handleAiGenerate}
            disabled={aiGenerating || !aiPrompt.trim()}
            className="w-full bg-gradient-to-r from-violet-600 to-cyan-600 hover:from-violet-700 hover:to-cyan-700 text-white"
            data-testid="ai-generate-btn"
          >
            {aiGenerating ? (
              <>
                <Loader2 className="animate-spin mr-2" size={18} />
                Generating... (may take up to 60s)
              </>
            ) : (
              <>
                <Wand2 size={18} className="mr-2" />
                Generate with AI
              </>
            )}
          </Button>

          {aiImage && (
            <div className="pt-4 border-t border-slate-700">
              <Button
                onClick={() => handleDownload('ai')}
                disabled={downloading === 'ai'}
                variant="outline"
                className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
              >
                {downloading === 'ai' ? <Loader2 className="animate-spin mr-2" size={18} /> : <Download size={18} className="mr-2" />}
                Download AI Logo
              </Button>
            </div>
          )}

          <p className="text-xs text-slate-500 text-center">
            Powered by AI image generation. Results may vary.
          </p>
        </div>
      )}
    </div>
  );
};

export default LogoDownload;
